import { DataReport, GenerateReportFormFields } from 'Core/common/DataReport/types'
import { ConfigItem, PreviewSections } from 'Core/interfaces'
import { ConfigFilterItem } from 'Core/interfaces/filters'

export enum ReportType {
	PDF = 0,
	EXCEL = 1
}

export enum ServerReportTypesInList {
	DATA2 = 'DATA2',
	INVOICE2 = 'INVOICE2'
}

export enum ReportSearchResultFields {
	searchById = 'searchById',
	searchByName = 'searchByName',
	searchByDescription = 'searchByDescription',
	searchByColumn = 'searchByColumn'
}

export enum ReportFilterFields {
	tagIds = 'tagIds',
	reportType = 'reportType',
	categoryId = 'categoryId',
	status = 'status',
	showOnlyFavorites = 'showOnlyFavorites'
}

export enum EditFormFields {
	name = 'name',
	type = 'type',
	status = 'status',
	tags = 'tags',
	description = 'description',
	lastChangeDate = 'lastChangeDate',
	lastUser = 'lastUser',
	comments = 'comments',
	fileName = 'fileName',
	category = 'category',
	selectClause = 'selectClause',
	fromClause = 'fromClause',
	whereClause = 'whereClause',
	groupByClause = 'groupByClause',
	orderByClause = 'orderByClause',
	columns = 'columns',
	isRestrictedByServiceSite = 'isRestrictedByServiceSite',
	serviceSiteIdColumn = 'serviceSiteIdColumn'
}

export enum SQLParameterFields {
	id = 'id',
	name = 'name',
	userType = 'userType',
	parameter = 'parameter',
	isRequired = 'isRequired',
	displayName = 'displayName'
}

export interface ReportParametersTypeOptions extends Entity {
	extra: PreviewSections | null
}

export enum WhereParametersFields {
	or = 'or',
	either = 'either',
	sqlField = 'sqlField',
	operator = 'operator',
	userType = 'userType',
	parameter = 'parameter',
	isRequired = 'isRequired',
	displayName = 'displayName',
	validationGroup = 'validationGroup'
}

export declare namespace Report {
	type BasicLabelValue<T = string> = LabelValue<T> | null
	type MostRecentExecution = {
		userName: BasicString
		dateTime: BasicString
	}
	interface BasicTag extends Entity<BasicString> {
		addressLine1?: BasicString
		addressLine2?: BasicString
		customerName?: BasicString
		serviceSiteName?: BasicString
		city?: BasicString
		code?: BasicString
		description?: BasicString
		stateCode?: BasicString
		dotLabel1FileName?: BasicString
		dotLabel2FileName?: BasicString
		dotLabel3FileName?: BasicString
		classCode?: BasicString
		psVendorId?: BasicString
		price?: number
		serviceSite?: BasicTag
		state?: BasicString
	}

	interface Basic extends Request.ReadList {}

	interface ParameterEntity extends Entity {
		parameterType: Entity
	}

	namespace Detailed {
		interface Invoice extends Request.Read.Invoice {}
		type Unknown = Invoice | DataReport.Data
	}

	namespace Request {
		interface Create {
			name: BasicString
			$type: DataReport.BasicType
			fileName?: BasicString
			categoryId?: BasicString
			description: BasicString
		}

		interface ReadList extends Entity<BasicString> {
			type: BasicString
			tags: null | Entity<BasicString>[]
			status: number
			columns: null | Entity<BasicString>[]
			category: null | Entity<BasicString>
			isFavorite: boolean
			description: BasicString
			mostRecentExecution: null | MostRecentExecution
		}

		namespace Read {
			interface Basic extends Entity<BasicString> {
				type: DataReport.BasicType
				status: DataReport.BasicStatus
				description: BasicString
				//TODO change tag type if required
				tags: BasicTag[]
				lastChangeDate: BasicString
				lastUser: BasicString
				comments: BasicString
			}

			interface Invoice extends Basic {
				fileName: BasicString
			}

			type Unknown = Invoice | DataReport.Detailed
		}

		namespace Update {
			interface Invoice {
				name: string
				description: BasicString
				status: DataReport.BasicStatus
				tags: { name: string }[]
				comments: BasicString
				fileName: string
				$type: 0
			}

			interface Data {
				name: string
				status: DataReport.BasicStatus
				tags: { name: string }[]
				categoryId: number | null
				xSelectClause: BasicString
				xFromClause: BasicString
				xWhereClause: BasicString
				xComments: BasicString
				xDescription: BasicString
				groupByClause: BasicString
				orderByClause: BasicString
				columns: BasicString
				isRestrictedByServiceSite: boolean
				serviceSiteIdColumn: BasicString
				$type: 1
			}

			type Unknown = Invoice | Data
		}

		interface ErrorMessages {
			FileName?: string
		}
	}

	interface Favorite {
		isFavorite: boolean
	}

	namespace Form {
		namespace Add {
			interface Basic {
				name: BasicString
				fileName: BasicString
				category: BasicString
				activeTab: DataReport.BasicType
				description: BasicString
			}

			interface DefaultValues extends Basic {
				name: null
				activeTab: 0
			}

			interface Valid extends Basic {
				name: NonNullable<BasicString>
				fileName: NonNullable<BasicString>
				category: NonNullable<BasicString>
			}
		}

		namespace Edit {
			interface Common {
				[EditFormFields.name]: BasicString
				[EditFormFields.type]: string
				[EditFormFields.status]: DataReport.BasicStatus
				[EditFormFields.tags]: string[]
				[EditFormFields.description]: BasicString
				[EditFormFields.lastChangeDate]: BasicString
				[EditFormFields.lastUser]: BasicString
				[EditFormFields.comments]: BasicString
			}

			namespace Invoice {
				interface Basic extends Common {
					[EditFormFields.fileName]: BasicLabelValue
				}

				interface Valid extends Basic {
					[EditFormFields.name]: NonNullable<BasicString>
					[EditFormFields.fileName]: NonNullable<BasicLabelValue>
				}
			}

			namespace Data {
				interface Basic extends Common {
					[EditFormFields.category]: BasicLabelValue<number>
					[EditFormFields.selectClause]: BasicString
					[EditFormFields.fromClause]: BasicString
					[EditFormFields.whereClause]: BasicString
					[EditFormFields.groupByClause]: BasicString
					[EditFormFields.orderByClause]: BasicString
					[EditFormFields.columns]: BasicString
					[EditFormFields.isRestrictedByServiceSite]: boolean
					[EditFormFields.serviceSiteIdColumn]: BasicString
				}

				interface Valid extends Basic {
					[EditFormFields.name]: NonNullable<BasicString>
				}
			}

			type UnknownValid = Invoice.Valid | Data.Valid
		}
	}

	namespace Preview {
		namespace Invoice {
			interface Form {
				[GenerateReportFormFields.invoiceId]: string | null
			}
			interface Request {
				invoiceId: Key
			}
		}
	}

	namespace Search {
		interface Config {
			[ReportSearchResultFields.searchById]: ConfigItem<ReportSearchResultFields>
			[ReportSearchResultFields.searchByName]: ConfigItem<ReportSearchResultFields>
			[ReportSearchResultFields.searchByDescription]: ConfigItem<ReportSearchResultFields>
			[ReportSearchResultFields.searchByColumn]: ConfigItem<ReportSearchResultFields>
		}
		interface Result {
			[ReportSearchResultFields.searchById]: Entity[]
			[ReportSearchResultFields.searchByName]: string[]
			[ReportSearchResultFields.searchByDescription]: string[]
			[ReportSearchResultFields.searchByColumn]: string[]
		}
	}
	namespace Filters {
		interface Config {
			[ReportFilterFields.tagIds]: ConfigFilterItem<string, string[]>
			[ReportFilterFields.reportType]: ConfigFilterItem<string, string>
			[ReportFilterFields.categoryId]: ConfigFilterItem<string, string>
			[ReportFilterFields.status]: ConfigFilterItem<string, string>
			[ReportFilterFields.showOnlyFavorites]: ConfigFilterItem<string, boolean>
		}
	}

	namespace SQLParameter {
		interface Basic extends Entity {
			userType: Entity | null
			parameter: Entity | null
			isRequired: boolean
			displayName: BasicString
		}

		interface Detailed extends Basic {}

		interface Request {
			name: string
			userTypeId: number | null
			isRequired: boolean
			parameterId: number
			displayName: string
		}

		namespace Form {
			interface Basic {
				[SQLParameterFields.name]: BasicString
				[SQLParameterFields.userType]: BasicLabelValue
				[SQLParameterFields.parameter]: BasicString
				[SQLParameterFields.isRequired]: boolean
				[SQLParameterFields.displayName]: BasicString
			}

			interface Valid extends Basic {
				[SQLParameterFields.name]: NonNullable<BasicString>
				[SQLParameterFields.parameter]: NonNullable<BasicString>
				[SQLParameterFields.displayName]: NonNullable<BasicString>
			}
		}
	}

	namespace WhereParameter {
		interface Basic {
			id: Key
			or: boolean
			either: boolean
			sqlField: BasicString
			operator: number | null
			userType: Entity | null
			parameter: Entity | null
			isRequired: boolean
			displayName: BasicString
			validationGroup: BasicString
		}

		interface Detailed extends Basic {}

		interface Request {
			or: boolean
			either: boolean
			sqlField: string
			operator: number | null
			isRequired: boolean
			userTypeId: number | null
			parameterId: number
			displayName: string
			validationGroup: BasicString
		}

		namespace Form {
			interface Basic {
				[WhereParametersFields.or]: boolean
				[WhereParametersFields.either]: boolean
				[WhereParametersFields.userType]: BasicLabelValue
				[WhereParametersFields.sqlField]: BasicString
				[WhereParametersFields.operator]: string
				[WhereParametersFields.parameter]: BasicString
				[WhereParametersFields.isRequired]: boolean
				[WhereParametersFields.displayName]: BasicString
				[WhereParametersFields.validationGroup]: BasicString
			}

			interface Valid extends Basic {
				[WhereParametersFields.userType]: NonNullable<BasicLabelValue>
				[WhereParametersFields.sqlField]: NonNullable<BasicString>
				[WhereParametersFields.parameter]: NonNullable<BasicString>
				[WhereParametersFields.displayName]: NonNullable<BasicString>
			}
		}
	}
	interface GenerateReportData {
		id: number
		isReportGenerated: boolean
		isEmailReportGenerated: boolean
	}
}
